const clampSeconds = (value, min = 2, max = 8) =>
  Math.max(min, Math.min(max, Number(value) || min));

const availableMinutes = (checkIn = {}) => {
  const minutes = Number(checkIn.minutes) || 3;
  if (minutes <= 3) return 2;
  if (minutes <= 7) return 3;
  return 5;
};

export function selectBreathingProtocol({ checkIn = {}, stateMatch = null } = {}) {
  const mobilization = Number(checkIn.intensity) || 3;
  const actionPressure = Number(checkIn.actionPressure) || 3;
  const energy = Number(checkIn.energy) || 3;
  const capture = Number(checkIn.cognitiveCapture) || 3;
  const recoveryDifficulty = Number(checkIn.recoveryDifficulty) || 3;
  const finalBand = Number(stateMatch?.finalBand) || 3;
  const durationMinutes = availableMinutes(checkIn);

  if (energy <= 2 && mobilization <= 3) {
    return {
      key: "balanced-gentle",
      title: "Gentle balanced breathing",
      inhaleSeconds: 3,
      exhaleSeconds: 3,
      durationMinutes,
      rationale:
        "Usable energy is limited, so APOS keeps the breathing rhythm balanced rather than emphasizing a long exhale that could feel overly downshifting.",
    };
  }

  if (
    mobilization >= 4 ||
    actionPressure >= 4 ||
    capture >= 4 ||
    recoveryDifficulty >= 4 ||
    finalBand >= 4
  ) {
    return {
      key: "extended-exhale",
      title: "Longer-exhale breathing",
      inhaleSeconds: 3,
      exhaleSeconds: 5,
      durationMinutes,
      rationale:
        "Your current nervous-system response carries more mobilization or action pressure, so APOS uses a comfortable longer exhale without breath holds.",
    };
  }

  return {
    key: "steady-exhale",
    title: "Steady state-matched breathing",
    inhaleSeconds: 4,
    exhaleSeconds: 5,
    durationMinutes,
    rationale:
      "Your current nervous-system response does not require a strong respiratory bias, so APOS uses a comfortable rhythm with a slightly longer exhale.",
  };
}

const somaticPlan = (checkIn = {}) => {
  const mobilization = Number(checkIn.intensity) || 3;
  const energy = Number(checkIn.energy) || 3;
  const actionPressure = Number(checkIn.actionPressure) || 3;
  const minutes = availableMinutes(checkIn);

  if (mobilization >= 4 || actionPressure >= 4) {
    return {
      title: "Low-intensity somatic grounding",
      activityKey: "somatic-grounding",
      activityType: "somatic",
      duration: `${minutes} minutes`,
      intensity: "Very light",
      instruction:
        "Use slow, deliberate shoulder, trunk, hand, and foot movement while keeping the range comfortable. Let the movement organize the nervous-system reaction without turning this into a workout.",
    };
  }

  if (energy <= 2) {
    return {
      title: "Gentle somatic mobilization",
      activityKey: "somatic-mobilization",
      activityType: "somatic",
      duration: `${minutes} minutes`,
      intensity: "Very light",
      instruction:
        "Use small seated or standing movements through the shoulders, trunk, hips, hands, and feet. Keep the rhythm easy and stop before fatigue rises.",
    };
  }

  return {
    title: "Low-intensity somatic movement",
    activityKey: "somatic-low-intensity",
    activityType: "somatic",
    duration: `${minutes} minutes`,
    intensity: "Very light",
    instruction:
      "Move slowly through a few comfortable shoulder, trunk, hip, hand, and foot movements. Keep attention on what changes in tension, breathing, and behavioral control rather than trying to create exercise intensity.",
  };
};

export function applyImmediateSupportMode(plan, checkIn = {}) {
  if (!plan) return plan;
  const mode = checkIn.supportMode || "auto";
  if (mode === "auto" || mode === "movement") return plan;

  if (mode === "breathing") {
    const protocol = selectBreathingProtocol({
      checkIn,
      stateMatch: plan.stateMatch,
    });
    return {
      ...plan,
      immediate: {
        ...plan.immediate,
        title: protocol.title,
        activityKey: "state-matched-breathing",
        activityType: "breathing",
        duration: `${protocol.durationMinutes} minutes`,
        intensity: "Very light",
        instruction: `Follow the breathing circle continuously. Breathe in for ${clampSeconds(protocol.inhaleSeconds)} seconds while the circle expands, then breathe out for ${clampSeconds(protocol.exhaleSeconds)} seconds while it shrinks. Keep the breath comfortable and do not hold it.`,
        breathingProtocol: protocol,
      },
      preparation: {
        ...plan.preparation,
        steps: (plan.preparation?.steps || []).filter(
          (step) => !/breath|breathing/i.test(String(step)),
        ),
      },
    };
  }

  if (mode === "somatic") {
    return {
      ...plan,
      immediate: {
        ...plan.immediate,
        ...somaticPlan(checkIn),
        breathingProtocol: null,
      },
      preparation: {
        ...plan.preparation,
        steps: (plan.preparation?.steps || []).filter(
          (step) => !/somatic|shoulder|trunk|hip movement/i.test(String(step)),
        ),
      },
    };
  }

  return plan;
}
